import React, { useEffect, useState } from "react";
import PageTitle from "../../PageTitle";
import LeaveNavigation from "./navigation/LeaveNavigation";
import LeaveMultiDatePicker from "./LeaveMultiDatePicker";
import useAxios from "../../../utilities/useAxios";
import { getCurrentUserProfile } from "../../../utilities/AuthService";
import {
	Form,
	Input,
	DatePicker,
	Select,
	Button,
	message,
	Card,
	ConfigProvider,
	Alert,
	Layout,
	Statistic,
	Row,
	Col,
	Typography,
	Checkbox,
} from "antd";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import locale from "antd/lib/locale/ko_KR";
import useIsMobile from "../../../hooks/useIsMobile";
import PageHeader from "../../common/PageHeader";

dayjs.locale("ko");

const { Option } = Select;
const { Content } = Layout;
const { Text } = Typography;

const formatLeaveDays = (value) => {
	const number = Number(value);
	if (!Number.isFinite(number)) {
		return "-";
	}
	const rounded = Math.round(number * 10) / 10;
	return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
};

const HALF_DAY_NOTICE = (
	<div>
		<p style={{ marginBottom: 8 }}>
			<strong>오전 반차</strong>: 당일 오후 1시까지 출퇴근
			단말기(카드/지문)에 출근 찍기를 해야 합니다. 오후 1시
			1분 전까지 찍지 않으면 연차 사용으로 간주합니다.
		</p>
		<p style={{ marginBottom: 0 }}>
			<strong>오후 반차</strong>: 그날 근무유형 출근 시각에
			맞춰 출근한 뒤, 12시에 출퇴근 단말기에 퇴근 찍기를 해야
			합니다. 출근 찍기와 12시 퇴근 찍기 중 하나라도 있으면
			0.5일이 유지되고, 둘 다 없으면 연차 사용으로 간주합니다.
		</p>
	</div>
);

const ApplyForAnnualLeave = () => {
	const isMobile = useIsMobile();
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [userProfile, setUserProfile] = useState(null);
	const [leaveType, setLeaveType] = useState(null);
	const [annualBalance, setAnnualBalance] = useState(null);
	const [holidayHalfRemaining, setHolidayHalfRemaining] = useState(0);
	const [holidayHalfCredits, setHolidayHalfCredits] = useState([]);
	const [useHolidayHalf, setUseHolidayHalf] = useState(false);

	useEffect(() => {
		message.config({ top: 150 });
		return () => message.config({ top: 8 });
	}, []);

	const fetchAnnualBalance = async () => {
		try {
			const response = await useAxios.get(
				"/utils/leave/annual-balance/me/",
			);
			if (response.status === 200) {
				setAnnualBalance(response.data);
			}
		} catch (e) {
			setAnnualBalance(null);
			message.error("남은 연차를 불러오지 못했습니다.");
		}
	};

	const fetchHolidayHalf = async () => {
		try {
			const response = await useAxios.get(
				"/utils/leave/holiday-half-day/",
				{ params: { mine: 1 } },
			);
			const today = dayjs().startOf("day");
			const usable = (response.data?.credits || []).filter(
				(credit) => {
					if (credit.is_used) return false;
					const until = dayjs(credit.expires_on).startOf(
						"day",
					);
					return (
						until.isValid() &&
						!until.isBefore(today, "day")
					);
				},
			);
			setHolidayHalfCredits(usable);
			setHolidayHalfRemaining(
				response.data?.remaining ?? usable.length,
			);
		} catch {
			setHolidayHalfRemaining(0);
			setHolidayHalfCredits([]);
		}
	};

	const renderHolidayHalfList = () => {
		if (holidayHalfRemaining < 1 || !holidayHalfCredits.length) {
			return null;
		}
		return (
			<ul
				style={{
					margin: "8px 0 0",
					paddingLeft: 18,
					listStyle: "disc",
				}}>
				{holidayHalfCredits.map((credit) => (
					<li key={credit.id} style={{ marginBottom: 4 }}>
						<Text type="secondary">
							{credit.reason || "사유 없음"}
							{" · 사용 기한 "}
							{dayjs(credit.expires_on).format(
								"YYYY-MM-DD",
							)}
						</Text>
					</li>
				))}
			</ul>
		);
	};

	useEffect(() => {
		(async () => {
			try {
				const profile = await getCurrentUserProfile();
				setUserProfile(profile);
				form.setFieldsValue({
					name: profile.name,
					position:
						profile.classification_name ===
						"교역자"
							? "교역자"
							: "교직원",
					application_date: dayjs(),
				});
			} catch (e) {
				message.error(
					"사용자 정보를 불러오는데 실패했습니다.",
				);
			}
			fetchAnnualBalance();
			fetchHolidayHalf();
		})();
	}, [form]);

	const onFinish = async (values) => {
		setLoading(true);
		try {
			const dates = (values.leave_dates || []).slice();
			if (!dates.length) {
				message.error("날짜를 하나 이상 선택해주세요.");
				return;
			}
			const formData = {
				leave_type: values.leave_type,
				leave_dates: dates
					.sort((a, b) => a.valueOf() - b.valueOf())
					.map((d) => d.format("YYYY-MM-DD")),
			};
			if (
				(values.leave_type === "오전반차" ||
					values.leave_type === "오후반차") &&
				useHolidayHalf
			) {
				formData.use_holiday_half_day = true;
			}
			const response = await useAxios.post(
				"/utils/leave/apply/",
				formData,
			);
			if (response.status === 201) {
				message.success("연차 신청이 완료되었습니다.");
				form.resetFields();
				if (userProfile) {
					form.setFieldsValue({
						name: userProfile.name,
						position:
							userProfile.classification_name ===
							"교역자"
								? "교역자"
								: "교직원",
						application_date: dayjs(),
					});
				}
				setLeaveType(null);
				setUseHolidayHalf(false);
				fetchAnnualBalance();
				fetchHolidayHalf();
			}
		} catch (error) {
			message.error(
				error.response?.data?.error ||
					"신청 처리 중 오류가 발생했습니다.",
			);
		} finally {
			setLoading(false);
		}
	};

	const disabledDate = (current) => {
		if (current && current.day() === 0) {
			return true;
		}
		return false;
	};

	const watchedDates = Form.useWatch("leave_dates", form) || [];
	const showHalfNotice =
		(leaveType === "오전반차" || leaveType === "오후반차") &&
		watchedDates.length === 1;

	useEffect(() => {
		if (!showHalfNotice && useHolidayHalf) {
			setUseHolidayHalf(false);
		}
	}, [showHalfNotice, useHolidayHalf]);

	return (
		<ConfigProvider locale={locale}>
			<PageTitle title="연차 신청" />
			<Layout className="member-layout">
				<LeaveNavigation />
				<Layout className="member-content-layout">
					<Content className="member-content">
						<div className="member-content__inner">
							<div className="member-content__form-container page leave-apply-form">
								<PageHeader title="연차 신청" />
								<Card className="surface-card">
									{annualBalance?.available && (
										<div
											style={{
												marginBottom: 16,
											}}>
											<Row
												gutter={
													isMobile
														? [0, 16]
														: [16, 16]
												}>
												<Col
													xs={12}
													sm={6}>
													<Statistic
														title="남은 연차"
														value={formatLeaveDays(
															annualBalance.remaining_days,
														)}
														suffix="일"
													/>
												</Col>
												<Col
													xs={12}
													sm={6}>
													<Statistic
														title="발생"
														value={formatLeaveDays(
															annualBalance.granted_days,
														)}
														suffix="일"
													/>
												</Col>
												<Col
													xs={12}
													sm={6}>
													<Statistic
														title="사용"
														value={formatLeaveDays(
															annualBalance.used_days,
														)}
														suffix="일"
													/>
												</Col>
												<Col
													xs={12}
													sm={6}>
													<Statistic
														title="다음 초기화"
														value={
															annualBalance.reset_date
														}
													/>
												</Col>
											</Row>
											{annualBalance.pending_days >
												0 && (
												<Text type="secondary">
													승인 대기{" "}
													{formatLeaveDays(
														annualBalance.pending_days,
													)}
													일이 남은
													연차에서 빠져
													있습니다.
												</Text>
											)}
											{annualBalance.carried_in_days >
												0 && (
												<div>
													<Text type="secondary">
														이전 기간에서
														이월된{" "}
														{formatLeaveDays(
															annualBalance.carried_in_days,
														)}
														일이 포함되어
														있습니다.
													</Text>
												</div>
											)}
											{annualBalance.prior_deduction_days >
												0 && (
												<div>
													<Text type="secondary">
														이전 사용{" "}
														{formatLeaveDays(
															annualBalance.prior_deduction_days,
														)}
														일이 빠져
														있습니다.
													</Text>
												</div>
											)}
											<div style={{ marginTop: 8 }}>
												<Text>
													남은 공휴일 반차{" "}
													<strong>
														{holidayHalfRemaining}
													</strong>
													개
												</Text>
												{renderHolidayHalfList()}
											</div>
										</div>
									)}
									{annualBalance &&
										!annualBalance.available && (
											<Alert
												type="warning"
												showIcon
												style={{
													marginBottom: 16,
												}}
												message="남은 연차를 확인할 수 없습니다."
												description="입사일이 등록된 교직원만 남은 연차를 조회할 수 있습니다."
											/>
										)}
									{(!annualBalance ||
										!annualBalance.available) && (
										<div style={{ marginBottom: 16 }}>
											<Text>
												남은 공휴일 반차{" "}
												<strong>
													{holidayHalfRemaining}
												</strong>
												개
											</Text>
											{renderHolidayHalfList()}
										</div>
									)}
									{showHalfNotice && (
										<Alert
											type="info"
											showIcon
											style={{
												marginBottom: 16,
											}}
											message="반차 출퇴근 단말기 안내"
											description={
												HALF_DAY_NOTICE
											}
										/>
									)}
									<Form
										form={form}
										layout="vertical"
										onFinish={onFinish}
										initialValues={{
											application_date:
												dayjs(),
										}}>
										<Form.Item
											name="name"
											label="이름">
											<Input disabled />
										</Form.Item>
										<Form.Item
											name="position"
											label="직분">
											<Select disabled>
												<Option value="교역자">
													교역자
												</Option>
												<Option value="교직원">
													교직원
												</Option>
											</Select>
										</Form.Item>
										<Form.Item
											name="leave_type"
											label="구분"
											rules={[
												{
													required: true,
													message:
														"구분을 선택해주세요",
												},
											]}>
											<Select
												placeholder="구분을 선택하세요"
												onChange={(v) => {
													setLeaveType(v);
													setUseHolidayHalf(
														false,
													);
													form.setFieldsValue({
														leave_dates: [],
													});
												}}>
												<Option value="연차">
													연차
												</Option>
												<Option value="오전반차">
													오전 반차
												</Option>
												<Option value="오후반차">
													오후 반차
												</Option>
											</Select>
										</Form.Item>
										<Form.Item
											name="application_date"
											label="신청일">
											<DatePicker
												inputReadOnly={isMobile}
												style={{
													width: "100%",
												}}
												disabled
												format="YYYY-MM-DD"
											/>
										</Form.Item>
										<Form.Item
											name="leave_dates"
											label="사용일"
											rules={[
												{
													required: true,
													type: "array",
													min: 1,
													message:
														"사용일을 하나 이상 선택해주세요",
												},
											]}>
											<LeaveMultiDatePicker
												disabledDate={
													disabledDate
												}
												placeholder="사용일을 선택하세요"
											/>
										</Form.Item>
										{showHalfNotice && (
											<Form.Item>
												<Checkbox
													checked={
														useHolidayHalf
													}
													disabled={
														holidayHalfRemaining <
														1
													}
													onChange={(e) =>
														setUseHolidayHalf(
															e.target
																.checked,
														)
													}>
													공휴일 반차 사용
													{holidayHalfRemaining <
													1
														? " (잔여 없음)"
														: ""}
												</Checkbox>
											</Form.Item>
										)}
										<Form.Item>
											<Button
												type="primary"
												htmlType="submit"
												loading={
													loading
												}
												block>
												신청하기
											</Button>
										</Form.Item>
									</Form>
								</Card>
							</div>
						</div>
					</Content>
				</Layout>
			</Layout>
		</ConfigProvider>
	);
};

export default ApplyForAnnualLeave;
